import { Category } from "./Category.js";
import { Locale, type LocalizedString, type LocalizedStringArray } from "./Locale.js";

export enum CardIcon {
	Pig = "Pig",
	Rabbit = "Rabbit",
	Magnifier = "Magnifier",
	Home = "Home",
}

export interface CardTexts {
	icon: CardIcon;
	title: LocalizedString;
	points: LocalizedString[];
}

export interface CategoryTexts {
	headline: LocalizedString;
	subheadline: LocalizedString;
	texts: LocalizedStringArray;
	cards: CardTexts[];
}

export const categoryTexts: Record<Category, CategoryTexts> = {
	[Category.Racks]: {
		headline: {
			[Locale.cs]: "Vertikální zakladače Compact",
			[Locale.en]: "Racks",
			[Locale.pl]: "Pionowe systemy magazynowe",
			[Locale.sk]: "Vertikálne zakladače",
		},
		subheadline: {
			[Locale.cs]: "Úspora místa, času a pracovních sil",
			[Locale.en]: "Space, time, and labor savings",
			[Locale.pl]: "Oszczędność miejsca, czasu i personelu",
			[Locale.sk]: "Úspora miesta, času a pracovnej sily",
		},
		texts: {
			[Locale.cs]: [
				"Vertikální zakladače (také označované jako věže nebo výtahové skladovací systémy) skladují zboží ve velké výšce bezpečně a zcela pod kontrolou. Tato technologie zajišťuje optimální využití prostoru, umožňuje úspory 70 až 90 % plochy v porovnání se standardním policovým nebo paletovým regálem a výrazné zrychlení toku materiálu.",
				"Naše věžové skladovací systémy, dodávané na klíč potřebám zákazníků, přinášejí viditelný efekt okamžitě po uvedení do provozu ve skladu. Automatický výtahový systém je jednoduchý, jeho obsluha je pohodlná a nevyžaduje časově náročnou údržbu. Umožňuje snížit pracovní náklady, udržuje efektivní přehled o položkách na skladě a přitom zabírá mnohem méně plochy než tradiční řešení. Při správné integraci dokáží vertikální zakladače výrazně zrychlit tok materiálu a jsou tak ideálním modulárním řešením například pro e-commerce.",
				"Návratnost investice do věží VertiFlex je rychlejší, než by se dalo očekávat. Většina projektů počítá s návratností 12 – 24 měsíců.",
			],
			[Locale.en]: [
				"Compact Vertical Storage Lift stores your articles vertically, securely and in a controlled way. Each Vertical Storage Lift is customised to make the optimum use of the space. You save 70-90% of the floor space compared to standard shelving and pallet racks.",
				"Our Racks, tailored to the customer‘s needs, deliver visible results immediately after commissioning in the warehouse.",
				"The automatic vertical storage lift is simple, convenient to operate and does not require time-consuming maintenance. It allows you to reduce labor costs, helps maintain order in the warehouse and at the same time takes up much less space than traditional solutions. With the right application, it can greatly speed up the flow of material, making it an ideal modular solution for e-commerce.",
				"The return on investment for Vertiflex Racks is faster than expected. Often within a year or two. The automatic vertical storage lift pays off!",
			],
			[Locale.pl]: [
				"Pionowe systemy magazynowe to nowoczesne rozwiązania logistyczne, które umożliwiają bezpieczne i kontrolowane przechowywanie artykułów w układzie pionowym. Dzięki zaawansowanej technologii nasze systemy zostały zaprojektowane z myślą o maksymalnym wykorzystaniu dostępnej przestrzeni magazynowej, co pozwala zaoszczędzić od 70% do 90% powierzchni w porównaniu z tradycyjnymi regałami półkowymi i paletowymi.",
				"Nasze pionowe windy magazynowe, w pełni dostosowane do indywidualnych potrzeb klientów, zapewniają widoczne korzyści już od momentu uruchomienia w magazynie.",
				"Automatyczne pionowe systemy magazynowe wyróżniają się prostotą obsługi, minimalnym zapotrzebowaniem na konserwację oraz znacznymi oszczędnościami przestrzeni magazynowej. Ich zastosowanie pozwala na przyspieszenie przepływu materiałów, obniżenie kosztów pracy oraz utrzymanie porządku w magazynie, co czyni je doskonałym wyborem, zwłaszcza dla branży e-commerce.",
				"Zwrot z inwestycji w pionowe windy magazynowe VertiFlex następuje szybciej, niż można się spodziewać – często już w ciągu roku lub dwóch od wdrożenia. To rozwiązanie, które nie tylko optymalizuje operacje magazynowe, ale także szybko zaczyna generować realne oszczędności. Automatyczna pionowa winda magazynowa to inwestycja, która się opłaca!",
			],
			[Locale.sk]: [
				"Vertikálne zakladače skladujú tovar vo veľkých výškach bezpečne a úplne pod kontrolou. Táto technológia zabezpečuje optimálne využitie priestoru, čím sa ušetrí 70 až 90 % plochy v porovnaní so štandardným regálom alebo paletovým regálom a výrazne sa urýchli tok materiálu.",
				"Naše vertikálne zakladače, dodávané podľa potrieb našich zákazníkov, prinášajú viditeľný efekt okamžite po uvedení do prevádzky v sklade. Automatický vertikálny zakladač sa jednoducho používa, pohodlne obsluhuje a nevyžaduje si časovo náročnú údržbu. Umožňuje znížiť náklady na pracovnú silu, udržiavať efektívny prehľad o položkách na sklade a zároveň zaberá oveľa menej miesta ako tradičné riešenia. Pri správnej integrácii dokáže výrazne zrýchliť tok materiálu, čo z neho robí ideálne modulárne riešenie napríklad pre elektronický obchod.",
				"Návratnosť investícií do vertikálnych zakladačov VertiFlex je rýchlejšia, než by sa dalo očakávať. Pri väčšine projektov sa očakáva doba návratnosti 12 - 24 mesiacov.",
			],
		},
		cards: [
			{
				icon: CardIcon.Pig,
				title: {
					[Locale.cs]: "Úspora nákladů",
					[Locale.en]: "Cost savings",
					[Locale.pl]: "Oszczędność kosztów",
					[Locale.sk]: "Úspora nákladov",
				},
				points: [
					{
						[Locale.cs]: "Nižší provozní náklady",
						[Locale.en]: "Lower operating costs",
						[Locale.pl]: "Niższe koszty operacyjne",
						[Locale.sk]: "Nižšie prevádzkové náklady",
					},
					{
						[Locale.cs]: "Rychlá návratnost investice",
						[Locale.en]: "Quick return on investment",
						[Locale.pl]: "Krótki czas zwrotu inwestycj",
						[Locale.sk]: "Rýchla návratnosť investície",
					},
					{
						[Locale.cs]: "Větší skladová plocha",
						[Locale.en]: "Larger storage area",
						[Locale.pl]: "Wzrost wydajności",
						[Locale.sk]: "Väčšia skladová plocha",
					},
					{
						[Locale.cs]: "Rychlá montáž a uvedení do provozu",
						[Locale.en]: "Quick assembly and commissioning",
						[Locale.pl]: "Szybka instalacja i uruchomienie",
						[Locale.sk]: "Rýchla montáž a uvedenie do prevádzky",
					},
					{
						[Locale.cs]: "Lepší kontrola uloženého zboží",
						[Locale.en]: "Better control of stored goods",
						[Locale.pl]: "Lepsza kontrola przechowywanych artykułów",
						[Locale.sk]: "Lepšia kontrola uloženého tovaru",
					},
					{
						[Locale.cs]: "Snížení reklamačních nákladů",
						[Locale.en]: "Reduction of complaint costs",
						[Locale.pl]: "Niższe koszty reklamacji",
						[Locale.sk]: "Zníženie reklamačných nákladov",
					},
					{
						[Locale.cs]: "Snížení pracovních nákladů",
						[Locale.en]: "Reduction of labor costs",
						[Locale.pl]: "Wydajność zależna od sezonu",
						[Locale.sk]: "Zníženie pracovných nákladov",
					},
				],
			},
			{
				icon: CardIcon.Rabbit,
				title: {
					[Locale.cs]: "Zrychlení procesů",
					[Locale.en]: "Process acceleration",
					[Locale.pl]: "Przyspieszenie procesów",
					[Locale.sk]: "Zrýchlenie procesov",
				},
				points: [
					{
						[Locale.cs]: "Kratší časy pro vychystání",
						[Locale.en]: "Shorter picking times",
						[Locale.pl]: "Krótsze czasy kompletacji",
						[Locale.sk]: "Kratšie časy pre vychystávanie",
					},
					{
						[Locale.cs]: "Doprava materiálu v prostojích",
						[Locale.en]: "Material transport in standstills",
						[Locale.pl]: "Transport materiałów w czasie przestojów",
						[Locale.sk]: "Doprava materiálu v prestojoch",
					},
					{
						[Locale.cs]: "Rychlejší kompletace",
						[Locale.en]: "Faster completion",
						[Locale.pl]: "Szybszy montaż",
						[Locale.sk]: "Rýchlejšia kompletácia",
					},
					{
						[Locale.cs]: "Vyšší produktivita skladu",
						[Locale.en]: "Higher warehouse productivity",
						[Locale.pl]: "Bardziej wydajna obsługa zapasów",
						[Locale.sk]: "Vyššia produktivita skladu",
					},
					{
						[Locale.cs]: "Kratší pochůzky operátorů",
						[Locale.en]: "Shorter operator walks",
						[Locale.pl]: "Krótsze odległości pokonywane przez operatorów",
						[Locale.sk]: "Kratšie pochôdzky operátorov",
					},
				],
			},
			{
				icon: CardIcon.Magnifier,
				title: {
					[Locale.cs]: "Lepší využití zdrojů",
					[Locale.en]: "Better resource utilization",
					[Locale.pl]: "Lepsze wykorzystanie zasobów",
					[Locale.sk]: "Lepšie využitie zdrojov",
				},
				points: [
					{
						[Locale.cs]: "Zlepšení kvality obsluhy",
						[Locale.en]: "Improving service quality",
						[Locale.pl]: "Wyższy poziom obsługi",
						[Locale.sk]: "Zlepšenie kvality obsluhy",
					},
					{
						[Locale.cs]: "Větší přesnost dodávek",
						[Locale.en]: "Greater delivery accuracy",
						[Locale.pl]: "Większa precyzja dostaw",
						[Locale.sk]: "Väčšia presnosť dodávok",
					},
					{
						[Locale.cs]: "Méně chyb při kompletaci",
						[Locale.en]: "Fewer errors in completion",
						[Locale.pl]: "Mniej nieprawidłowych pobrań",
						[Locale.sk]: "Menej chýb pri kompletácii.",
					},
					{
						[Locale.cs]: "Kompletace typu k člověku",
						[Locale.en]: "Completion type to human",
						[Locale.pl]: "Pływające lokalizacje magazynowe",
						[Locale.sk]: "Kompletácia typom ku človeku.",
					},
					{
						[Locale.cs]: "Větší možnosti sledování zboží",
						[Locale.en]: "Greater tracking options",
						[Locale.pl]: "Zwiększona identyfikowalność",
						[Locale.sk]: "Väčšia možnosť sledovania tovaru",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Lepší pracovní prostředí",
					[Locale.en]: "Better working environment",
					[Locale.pl]: "Lepsze środowisko pracy",
					[Locale.sk]: "Lepšie pracovné prostredie",
				},
				points: [
					{
						[Locale.cs]: "Krátký čas na proškolení",
						[Locale.en]: "Short training time",
						[Locale.pl]: "Krótki czas szkolenia",
						[Locale.sk]: "Krátky čas na preškolenie",
					},
					{
						[Locale.cs]: "Lepší ergonomie práce",
						[Locale.en]: "Better ergonomics of work",
						[Locale.pl]: "Lepsza ergonomia",
						[Locale.sk]: "Lepšia ergonómia práce",
					},
					{
						[Locale.cs]: "Méně absencí z důvodu nemoci",
						[Locale.en]: "Less sick leave",
						[Locale.pl]: "Mniej zwolnień lekarskich",
						[Locale.sk]: "Menej absencie z dôvodu choroby",
					},
					{
						[Locale.cs]: "Příslušenství pro kompletaci usnadňující vyhledávání zboží",
						[Locale.en]: "Completion accessories facilitating the search for goods",
						[Locale.pl]: "Elastyczny wybór metod kompletacji",
						[Locale.sk]: "Príslušenstvo pre kompletáciu ulahčujúcu vyhľadávanie tovaru",
					},
					{
						[Locale.cs]: "Menší riziko zraňení a úrazu",
						[Locale.en]: "Less risk of injury and accident",
						[Locale.pl]: "Zmniejszone ryzyko obrażeń ciała",
						[Locale.sk]: "Menšie riziko zranenia a úrazu",
					},
				],
			},
		],
	},
	[Category.Cranes]: {
		headline: {
			[Locale.cs]: "Zakladače a shuttle systémy",
			[Locale.en]: "Cranes and shuttle systems",
			[Locale.pl]: "Układnice i systemy transferowe",
			[Locale.sk]: "Žeriavy a kyvadlové systémy",
		},
		subheadline: {
			[Locale.cs]: "Automatizované skladovací systémy pro zvýšení efektivity a kvality ve skladech",
			[Locale.en]: "Automated storage and retrieval systems to increase efficiency and quality in warehouses",
			[Locale.pl]: "Zautomatyzowane systemy magazynowania i pobierania zwiększające wydajność i jakość w magazynach",
			[Locale.sk]: "Automatizované skladové systémy na zvýšenie efektívnosti a kvality v skladoch",
		},
		texts: {
			[Locale.cs]: [
				"Plně automatizované systémy zvyšují kvalitu, flexibilitu a efektivitu skladu. Nabízíme širokou škálu technologií pro automatizované skladování a vychystávání palet a menšího zboží, jako jsou kartony nebo přepravky.",
				"Automatizovaná skladovací řešení musí být škálovatelná a zároveň maximalizovat využití prostoru a mít odpovídající výkon. Zásadní je také být dostatečně flexibilní, aby bylo možné reagovat na měnící se požadavky trhu a skladbu uloženého sortimentu. Prostor vás stojí peníze! Proto vždy najdeme nejefektivnější řešení s nejlepším poměrem ceny a výkonu, transparentními procesy a největší flexibilitou. Na měnící se podmínky je tak možné kdykoliv reagovat úpravou dostupných kapacit, když je třeba navýšit propustnost skladu.",
				"Automatizovaná řešení pro zásilky a kusové zboží – Dynamické, prostorově úsporné a flexibilní řešení pro skladování drobných dílů, ať už v přepravkách, kartonech nebo kontejnerech! Automatický miniload systém je určen pro efektivní skladování malých a středně velkých položek s vysokou propustností. Tornado miniload a CycloneCarrier shuttle systém jsou ideální kombinací pro rychlý a efektivní provoz.",
				"Automatizovaná řešení pro palety a těžké náklady – Efektivní řešení pro těžké a objemné zboží, které je třeba skladovat rychle, bezpečně a s optimálním využitím prostoru! Automatizovaný paletový sklad lze použít univerzálněji než jakoukoli jinou skladovací technologii. Ať už skladujete v teplotách od -30 °C do +45 °C nebo je váš sklad vysoký 6 až 45 metrů, jsou PowerStore a Vectura ideálním řešením pro rychlý, bezpečný a zdrojově efektivní tok materiálu a skladování zboží až do celkové hmotnosti 3 500 kilogramů.",
			],
			[Locale.en]: [
				"Fully automated systems increase the quality, flexibility and efficiency of the warehouse. We offer a wide range of technologies for automated storage and picking of pallets and smaller goods such as cartons or crates.",
				"Automated warehouse solutions must be scalable while maximizing space utilization and having adequate performance. It is also essential to be flexible enough to respond to changing market demands and the mix of stored product. Space costs you money! That's why we always find the most efficient solution with the best value for money, transparent processes and the greatest flexibility. You can react to changing conditions by adjusting the available capacity at any time when you need to increase your warehouse throughput.",
				"Automated solutions for small loads – A dynamic, space-saving and flexible solution for storing small parts, whether in crates, cartons or containers, the automated miniload system is designed for efficient storage of small and medium-sized items with high throughput. The Tornado miniload and CycloneCarrier shuttle system are the ideal combination for fast and efficient operation.",
				"Automated solutions for pallets and heavy loads – An efficient solutions for heavy and bulky goods that need to be stored quickly, safely and with optimal use of space. Automated pallet storage can be used more universally than any other storage technology. Whether you store in temperatures from -30°C to +45°C or your warehouse is six or 45 metres high at most, PowerStore and Vectura are the ideal combination for fast, safe and resource-efficient material flow and storage of goods up to a total weight of 3,500 kilograms.",
			],
			[Locale.pl]: [
				"W pełni zautomatyzowane systemy magazynowe znacząco poprawiają jakość, elastyczność oraz wydajność operacyjną magazynów. Oferujemy szeroką gamę technologii, które umożliwiają automatyczne składowanie i pobieranie zarówno palet, jak i mniejszych towarów, takich jak kartony czy skrzynki.",
				"Zautomatyzowane rozwiązania magazynowe muszą być skalowalne, aby maksymalizować wykorzystanie przestrzeni przy jednoczesnym zachowaniu odpowiedniej wydajności. Ważnym aspektem jest również ich elastyczność, pozwalająca dostosować się do zmieniających się wymagań rynku oraz różnorodnego asortymentu produktów. Przestrzeń magazynowa generuje koszty, dlatego zawsze oferujemy rozwiązania charakteryzujące się najlepszym stosunkiem jakości do ceny, przejrzystością procesów oraz maksymalną elastycznością. Nasze systemy pozwalają na szybkie reagowanie na zmienne warunki i łatwe dostosowanie pojemności magazynowej w momencie, gdy konieczne jest zwiększenie przepustowości.",
				"Zautomatyzowane rozwiązania dla małych ładunków - Dynamiczne, oszczędzające miejsce i elastyczne, systemy Miniload Tornado i CycloneCarrier to zaawansowane rozwiązania przeznaczone do przechowywania małych i średnich części w skrzyniach, kartonach lub pojemnikach. Dzięki dużej przepustowości umożliwiają szybkie i wydajne operacje magazynowe, doskonale sprawdzając się w środowiskach wymagających wysokiej efektywności.\n" +
					"To idealne połączenie technologii magazynowych zapewnia optymalne wykorzystanie przestrzeni, znacząco przyspieszając procesy składowania i pobierania, co czyni je niezastąpionym wyborem dla nowoczesnych magazynów.\n",
				"Zautomatyzowane rozwiązania dla palet i ciężkich ładunków - PowerStore i Vectura to zaawansowane zautomatyzowane systemy magazynowe, idealne do przechowywania ciężkich i nieporęcznych towarów. Zapewniają szybkie, bezpieczne i optymalne wykorzystanie przestrzeni magazynowej, oferując wszechstronność niedostępną dla innych technologii magazynowania.\n" +
					"Systemy te sprawdzają się w szerokim zakresie temperatur – od -30°C do +45°C – oraz w magazynach o wysokości od 6 do nawet 45 metrów. Dzięki temu są odpowiednie dla różnych branż i wymagających środowisk operacyjnych.\n" +
					"PowerStore i Vectura wspólnie gwarantują szybki i zasobooszczędny przepływ materiałów oraz przechowywanie towarów o wadze do 3,500 kilogramów. To rozwiązanie łączy w sobie najwyższą wydajność z bezpieczeństwem i efektywnością, co sprawia, że stanowi idealne rozwiązanie dla nowoczesnych magazynów.",
			],
			[Locale.sk]: [
				"Plne automatizované systémy zvyšujú kvalitu, flexibilitu a efektívnosť vášho skladu. Ponúkame širokú škálu technológií na automatizované skladovanie a vychystávanie paliet a menšieho tovaru, ako sú kartóny alebo prepravky.",
				"Automatizované skladové riešenia musia byť škálovateľné, pričom musia maximálne využívať priestor a mať primeraný výkon. Takisto je nevyhnutné, aby boli dostatočne flexibilné a dokázali reagovať na meniace sa požiadavky trhu a sortiment skladovaných výrobkov. Priestor vás stojí peniaze! Preto vždy nájdeme najefektívnejšie riešenie s najlepším pomerom ceny a kvality, transparentnými procesmi a najväčšou flexibilitou. Na meniace sa podmienky môžete reagovať úpravou dostupnej kapacity kedykoľvek, keď potrebujete zvýšiť priepustnosť skladu.",
				"Automatizované riešenia pre zásielky a kusový tovar - Dynamické, priestorovo úsporné a flexibilné riešenia skladovania malých dielov, či už v prepravkách, kartónoch alebo kontajneroch! Automatizovaný systém miniload je určený na efektívne skladovanie malých a stredne veľkých položiek s vysokou priepustnosťou. Miniload Tornado a kyvadlový systém CycloneCarrier sú ideálnou kombináciou na rýchlu a efektívnu prevádzku. Automatizované riešenia pre palety a ťažké náklady. Efektívne riešenia pre ťažký a objemný tovar, ktorý je potrebné uskladniť rýchlo, bezpečne a s optimálnym využitím priestoru! Automatizované skladovanie paliet sa dá použiť univerzálnejšie ako akákoľvek iná skladovacia technológia. Či už skladujete pri teplotách od -30 °C do +45 °C, alebo je váš sklad vysoký 6 až 45 metrov, PowerStore a Vectura sú ideálnym riešením na rýchly, bezpečný a zdrojovo efektívny tok materiálu a skladovanie tovaru do celkovej hmotnosti 3 500 kg.",
			],
		},
		cards: [
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Úspora nákladů",
					[Locale.en]: "Cost savings",
					[Locale.pl]: "Oszczędność kosztów",
					[Locale.sk]: "Úspora nákladov",
				},
				points: [
					{
						[Locale.cs]: "Nižší provozní náklady",
						[Locale.en]: "Lower operating costs",
						[Locale.pl]: "Niższe koszty operacyjne",
						[Locale.sk]: "Nižšie prevádzkové náklady",
					},
					{
						[Locale.cs]: "Rychlá návratnost investice",
						[Locale.en]: "Quick return on investment",
						[Locale.pl]: "Krótki czas zwrotu inwestycj",
						[Locale.sk]: "Rýchla návratnosť investície",
					},
					{
						[Locale.cs]: "Větší skladová plocha",
						[Locale.en]: "Larger storage area",
						[Locale.pl]: "Wzrost wydajności",
						[Locale.sk]: "Väčšia skladová plocha",
					},
					{
						[Locale.cs]: "Rychlá montáž a uvedení do provozu",
						[Locale.en]: "Quick assembly and commissioning",
						[Locale.pl]: "Szybka instalacja i uruchomienie",
						[Locale.sk]: "Rýchla montáž a uvedenie do prevádzky",
					},
					{
						[Locale.cs]: "Lepší kontrola uloženého zboží",
						[Locale.en]: "Better control of stored goods",
						[Locale.pl]: "Lepsza kontrola przechowywanych artykułów",
						[Locale.sk]: "Lepšia kontrola uloženého tovaru",
					},
					{
						[Locale.cs]: "Snížení reklamačních nákladů",
						[Locale.en]: "Reduction of complaint costs",
						[Locale.pl]: "Niższe koszty reklamacji",
						[Locale.sk]: "Zníženie reklamačných nákladov",
					},
					{
						[Locale.cs]: "Snížení pracovních nákladů",
						[Locale.en]: "Reduction of labor costs",
						[Locale.pl]: "Wydajność zależna od sezonu",
						[Locale.sk]: "Zníženie pracovných nákladov",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Zrychlení procesů",
					[Locale.en]: "Process acceleration",
					[Locale.pl]: "Przyspieszenie procesów",
					[Locale.sk]: "Zrýchlenie procesov",
				},
				points: [
					{
						[Locale.cs]: "Kratší časy pro vychystání",
						[Locale.en]: "Shorter picking times",
						[Locale.pl]: "Krótsze czasy kompletacji",
						[Locale.sk]: "Kratšie časy pre vychystávanie",
					},
					{
						[Locale.cs]: "Doprava materiálu v prostojích",
						[Locale.en]: "Material transport in standstills",
						[Locale.pl]: "Transport materiałów w czasie przestojów",
						[Locale.sk]: "Doprava materiálu v prestojoch",
					},
					{
						[Locale.cs]: "Rychlejší kompletace",
						[Locale.en]: "Faster completion",
						[Locale.pl]: "Szybszy montaż",
						[Locale.sk]: "Rýchlejšia kompletácia",
					},
					{
						[Locale.cs]: "Vyšší produktivita skladu",
						[Locale.en]: "Higher warehouse productivity",
						[Locale.pl]: "Bardziej wydajna obsługa zapasów",
						[Locale.sk]: "Vyššia produktivita skladu",
					},
					{
						[Locale.cs]: "Kratší pochůzky operátorů",
						[Locale.en]: "Shorter operator walks",
						[Locale.pl]: "Krótsze odległości pokonywane przez operatorów",
						[Locale.sk]: "Kratšie pochôdzky operátorov",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Lepší využití zdrojů",
					[Locale.en]: "Better resource utilization",
					[Locale.pl]: "Lepsze wykorzystanie zasobów",
					[Locale.sk]: "Lepšie využitie zdrojov",
				},
				points: [
					{
						[Locale.cs]: "Zlepšení kvality obsluhy",
						[Locale.en]: "Improving service quality",
						[Locale.pl]: "Wyższy poziom obsługi",
						[Locale.sk]: "Zlepšenie kvality obsluhy",
					},
					{
						[Locale.cs]: "Větší přesnost dodávek",
						[Locale.en]: "Greater delivery accuracy",
						[Locale.pl]: "Większa precyzja dostaw",
						[Locale.sk]: "Väčšia presnosť dodávok",
					},
					{
						[Locale.cs]: "Méně chyb při kompletaci",
						[Locale.en]: "Fewer errors in completion",
						[Locale.pl]: "Mniej nieprawidłowych pobrań",
						[Locale.sk]: "Menej chýb pri kompletácii",
					},
					{
						[Locale.cs]: "Kompletace typu k člověku",
						[Locale.en]: "Completion type to human",
						[Locale.pl]: "Pływające lokalizacje magazynowe",
						[Locale.sk]: "Kompletácia typom ku človeku",
					},
					{
						[Locale.cs]: "Větší možnosti sledování zboží",
						[Locale.en]: "Greater tracking options",
						[Locale.pl]: "Zwiększona identyfikowalność",
						[Locale.sk]: "Väčšia možnosť sledovania tovaru",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Lepší pracovní prostředí",
					[Locale.en]: "Better working environment",
					[Locale.pl]: "Lepsze środowisko pracy",
					[Locale.sk]: "Lepšie pracovné prostredie",
				},
				points: [
					{
						[Locale.cs]: "Krátký čas na proškolení",
						[Locale.en]: "Short training time",
						[Locale.pl]: "Krótki czas szkolenia",
						[Locale.sk]: "Krátky čas na preškolenie",
					},
					{
						[Locale.cs]: "Lepší ergonomie práce",
						[Locale.en]: "Better ergonomics of work",
						[Locale.pl]: "Lepsza ergonomia",
						[Locale.sk]: "Lepšia ergonómia práce",
					},
					{
						[Locale.cs]: "Méně absencí z důvodu nemoci",
						[Locale.en]: "Less sick leave",
						[Locale.pl]: "Mniej zwolnień lekarskich",
						[Locale.sk]: "Menej absencie kvôli chorobe",
					},
					{
						[Locale.cs]: "Příslušenství pro kompletaci usnadňující vyhledávání zboží",
						[Locale.en]: "Completion accessories facilitating the search for goods",
						[Locale.pl]: "Elastyczny wybór metod kompletacji",
						[Locale.sk]: "Príslušenstvo pre kompletáciu uľahčujúce vyhladávanie tovaru",
					},
					{
						[Locale.cs]: "Menší riziko zraňení a úrazu",
						[Locale.en]: "Less risk of injury and accident",
						[Locale.pl]: "Zmniejszone ryzyko obrażeń ciała",
						[Locale.sk]: "Menšie riziko zranenia a úrazu",
					},
				],
			},
		],
	},
	[Category.Robots]: {
		headline: {
			[Locale.cs]: "Roboti Geek+",
			[Locale.en]: "Geek+ Robots",
			[Locale.pl]: "Roboty Geek+",
			[Locale.sk]: "Roboty Geek+",
		},
		subheadline: {
			[Locale.cs]: "Inovativní robotická technologie pro automatizaci skladů",
			[Locale.en]: "Innovative robotic technology for warehouse automation",
			[Locale.pl]: "Innowacyjna technologia robotyczna do automatyzacji magazynów",
			[Locale.sk]: "Inovatívna robotická technológia pre automatizáciu skladu",
		},
		texts: {
			[Locale.cs]: [
				"Naše řešení využívající robotů Geek+ představuje špičkovou technologickou platformu zaměřenou na inteligentní robotická řešení, která transformují skladové operace. Tato pokročilá technologie využívá moderní roboty navržené pro různé činnosti, včetně vychystávání, třídění, dopravy a skladování zboží.",
				"Díky použití umělé inteligence a pokročilých algoritmů roboty zajišťují vysokou úroveň přesnosti a efektivity. Umožňují rychlejší a flexibilnější zpracování objednávek, čímž snižují provozní náklady a zvyšují produktivitu skladu.",
				"Celý systém robotů je navíc modulární a škálovatelný, což umožňuje snadné přizpůsobení měnícím se potřebám podnikání. Je ideální volbou pro sklady, které chtějí optimalizovat své procesy a udržet krok s rychle se měnícím trhem. S roboty získává vaše logistika výhodu ve flexibilitě a rychlosti, a to bez ohledu na velikost skladu nebo odvětví, ve kterém působíte.",
			],
			[Locale.en]: [
				"Our Geek+ robot solution is a cutting-edge technology platform focused on intelligent robotic solutions that transform warehouse operations. This advanced technology uses state-of-the-art robots designed for a variety of activities including picking, sorting, transporting and storing goods.",
				"Using artificial intelligence and advanced algorithms, the robots ensure high levels of accuracy and efficiency. They enable faster and more flexible order processing, reducing operating costs and increasing warehouse productivity.",
				"In addition, the entire robot system is modular and scalable, making it easy to adapt to changing business needs. It is the ideal choice for warehouses that want to optimize their processes and keep up with a rapidly changing market. With robots, your logistics department gains an advantage in flexibility and speed, regardless of the size of your warehouse or the industry you operate in.",
			],
			[Locale.pl]: [
				"Geek+ to zaawansowana platforma technologiczna, która transformuje operacje magazynowe dzięki inteligentnym rozwiązaniom robotycznym. Systemy Geek+ zostały zaprojektowane z myślą o automatyzacji kluczowych procesów, takich jak kompletacja, sortowanie, transport i przechowywanie towarów, co znacząco zwiększa efektywność oraz precyzję operacyjną.",
				"Wykorzystując najnowsze technologie sztucznej inteligencji i zaawansowane algorytmy, roboty Geek+ gwarantują wyjątkową dokładność i wydajność. Umożliwiają szybsze przetwarzanie zamówień, większą elastyczność operacyjną oraz redukcję kosztów, co czyni je idealnym wyborem dla nowoczesnych magazynów.",
				"System robotyczny Geek+ charakteryzuje się modułową i skalowalną konstrukcją, co pozwala na łatwe dostosowanie go do zmieniających się potrzeb biznesowych. To optymalne rozwiązanie dla magazynów, które pragną zoptymalizować swoje procesy i sprostać dynamicznym wyzwaniom rynku. Dzięki tej technologii logistyka zyskuje przewagę w zakresie szybkości, elastyczności oraz efektywności, niezależnie od wielkości magazynu czy specyfiki branży.",
			],
			[Locale.sk]: [
				"Naše robotické riešenie Geek+ je špičková technologická platforma zameraná na inteligentné robotické riešenia, ktoré menia skladové operácie. Táto pokročilá technológia využíva najmodernejšie roboty určené na rôzne činnosti vrátane vychystávania, triedenia, prepravy a skladovania tovaru.",
				"Pomocou umelej inteligencie a pokročilých algoritmov roboty zabezpečujú vysokú úroveň presnosti a efektívnosti. Umožňujú rýchlejšie a flexibilnejšie spracovanie objednávok, znižujú prevádzkové náklady a zvyšujú produktivitu skladu.",
				"Okrem toho je celý robotický systém modulárny a škálovateľný, takže sa dá ľahko prispôsobiť meniacim sa potrebám podniku. Je ideálnou voľbou pre sklady, ktoré chcú optimalizovať svoje procesy a držať krok s rýchlo sa meniacim trhom. S robotmi získa vaše logistické oddelenie výhodu v podobe flexibility a rýchlosti bez ohľadu na veľkosť skladu alebo odvetvie, v ktorom pôsobíte.",
			],
		},
		cards: [
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Úspora nákladů",
					[Locale.en]: "Cost savings",
					[Locale.pl]: "Oszczędność kosztów",
					[Locale.sk]: "Úspora nákladov",
				},
				points: [
					{
						[Locale.cs]: "Nižší provozní náklady",
						[Locale.en]: "Lower operating costs",
						[Locale.pl]: "Niższe koszty operacyjne",
						[Locale.sk]: "Nižšie prevádzkové náklady",
					},
					{
						[Locale.cs]: "Rychlá návratnost investice",
						[Locale.en]: "Quick return on investment",
						[Locale.pl]: "Krótki czas zwrotu inwestycj",
						[Locale.sk]: "Rýchla návratnosť investície",
					},
					{
						[Locale.cs]: "Větší skladová plocha",
						[Locale.en]: "Larger storage area",
						[Locale.pl]: "Wzrost wydajności",
						[Locale.sk]: "Väčšia skladová plocha",
					},
					{
						[Locale.cs]: "Rychlá montáž a uvedení do provozu",
						[Locale.en]: "Quick assembly and commissioning",
						[Locale.pl]: "Szybka instalacja i uruchomienie",
						[Locale.sk]: "Rýchla montáž a uvedenie do prevádzky",
					},
					{
						[Locale.cs]: "Lepší kontrola uloženého zboží",
						[Locale.en]: "Better control of stored goods",
						[Locale.pl]: "Lepsza kontrola przechowywanych artykułów",
						[Locale.sk]: "Lepšia kontrola uloženého tovaru",
					},
					{
						[Locale.cs]: "Snížení reklamačních nákladů",
						[Locale.en]: "Reduction of complaint costs",
						[Locale.pl]: "Niższe koszty reklamacji",
						[Locale.sk]: "Zníženie reklamačných nákladov",
					},
					{
						[Locale.cs]: "Snížení pracovních nákladů",
						[Locale.en]: "Reduction of labor costs",
						[Locale.pl]: "Wydajność zależna od sezonu",
						[Locale.sk]: "Zníženie pracovných nákladov",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Zrychlení procesů",
					[Locale.en]: "Process acceleration",
					[Locale.pl]: "Przyspieszenie procesów",
					[Locale.sk]: "Zrýchlenie procesov",
				},
				points: [
					{
						[Locale.cs]: "Kratší časy pro vychystání",
						[Locale.en]: "Shorter picking times",
						[Locale.pl]: "Krótsze czasy kompletacji",
						[Locale.sk]: "Kratšie časy pre vychystávania",
					},
					{
						[Locale.cs]: "Doprava materiálu v prostojích",
						[Locale.en]: "Material transport in standstills",
						[Locale.pl]: "Transport materiałów w czasie przestojów",
						[Locale.sk]: "Doprava materiálu v prestojoch",
					},
					{
						[Locale.cs]: "Rychlejší kompletace",
						[Locale.en]: "Faster completion",
						[Locale.pl]: "Szybszy montaż",
						[Locale.sk]: "Rýchlejšia kompletácia",
					},
					{
						[Locale.cs]: "Vyšší produktivita skladu",
						[Locale.en]: "Higher warehouse productivity",
						[Locale.pl]: "Bardziej wydajna obsługa zapasów",
						[Locale.sk]: "Vyššia produktivita skladu",
					},
					{
						[Locale.cs]: "Kratší pochůzky operátorů",
						[Locale.en]: "Shorter operator walks",
						[Locale.pl]: "Krótsze odległości pokonywane przez operatorów",
						[Locale.sk]: "Kratšie pochôdzky operátorov",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Lepší využití zdrojů",
					[Locale.en]: "Better resource utilization",
					[Locale.pl]: "Lepsze wykorzystanie zasobów",
					[Locale.sk]: "Lepšie využitie zdrojov",
				},
				points: [
					{
						[Locale.cs]: "Zlepšení kvality obsluhy",
						[Locale.en]: "Improving service quality",
						[Locale.pl]: "Wyższy poziom obsługi",
						[Locale.sk]: "Zlepšenie kvality obsluhy",
					},
					{
						[Locale.cs]: "Větší přesnost dodávek",
						[Locale.en]: "Greater delivery accuracy",
						[Locale.pl]: "Większa precyzja dostaw",
						[Locale.sk]: "Väčšia presnosť dodávok",
					},
					{
						[Locale.cs]: "Méně chyb při kompletaci",
						[Locale.en]: "Fewer errors in completion",
						[Locale.pl]: "Mniej nieprawidłowych pobrań",
						[Locale.sk]: "Menej chýb pri kompletácii",
					},
					{
						[Locale.cs]: "Kompletace typu k člověku",
						[Locale.en]: "Completion type to human",
						[Locale.pl]: "Pływające lokalizacje magazynowe",
						[Locale.sk]: "Kompletácia typom ku človeku",
					},
					{
						[Locale.cs]: "Větší možnosti sledování zboží",
						[Locale.en]: "Greater tracking options",
						[Locale.pl]: "Zwiększona identyfikowalność",
						[Locale.sk]: "Väčšia možnosť sledovania tovaru",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Lepší pracovní prostředí",
					[Locale.en]: "Better working environment",
					[Locale.pl]: "Lepsze środowisko pracy",
					[Locale.sk]: "Lepšie pracovné prostredie",
				},
				points: [
					{
						[Locale.cs]: "Krátký čas na proškolení",
						[Locale.en]: "Short training time",
						[Locale.pl]: "Krótki czas szkolenia",
						[Locale.sk]: "Krátky čas na preškolenie",
					},
					{
						[Locale.cs]: "Lepší ergonomie práce",
						[Locale.en]: "Better ergonomics of work",
						[Locale.pl]: "Lepsza ergonomia",
						[Locale.sk]: "Lepšia ergonómia",
					},
					{
						[Locale.cs]: "Méně absencí z důvodu nemoci",
						[Locale.en]: "Less sick leave",
						[Locale.pl]: "Mniej zwolnień lekarskich",
						[Locale.sk]: "Menšia absencia z dôvodu choroby",
					},
					{
						[Locale.cs]: "Příslušenství pro kompletaci usnadňující vyhledávání zboží",
						[Locale.en]: "Completion accessories facilitating the search for goods",
						[Locale.pl]: "Elastyczny wybór metod kompletacji",
						[Locale.sk]: "Príslušenstvo pre kompletáciu zjednodušujúce vyhľadávanie tovaru",
					},
					{
						[Locale.cs]: "Menší riziko zraňení a úrazu",
						[Locale.en]: "Less risk of injury and accident",
						[Locale.pl]: "Zmniejszone ryzyko obrażeń ciała",
						[Locale.sk]: "Menšie riziko zranenia a úrazu",
					},
				],
			},
		],
	},
	[Category.VertinodeAi]: {
		headline: {
			[Locale.cs]: "VertiNode®",
			[Locale.en]: "VertiNode®",
			[Locale.pl]: "VertiNode®",
			[Locale.sk]: "Vertinode®",
		},
		subheadline: {
			[Locale.cs]: "Speciální software VertiNode® pro automatizovanou logistiku",
			[Locale.en]: "Special software VertiNode® for automated logistics",
			[Locale.pl]: "Specjalne oprogramowanie dla zautomatyzowanej logistyki",
			[Locale.sk]: "Špeciálny softvér VertiNode pre automatizovanú logistiku",
		},
		texts: {
			[Locale.cs]: [
				"Software pro automatizovanou logistiku s nejnovější technologií umělé inteligence a kompatibilní s většinou skladovacích systémů",
				"VertiNode® má mnoho použití, ale vynikne především v prostředí e-commerce. Komplexní řešení dovolí integrovat výstupy z AI přímo do procesů společnosti. ERP systém se může od VertiNode® dozvědět okamžitě po předání zakázky k vyskladnění cílovou vyskladňovací pozici a očekávaný čas zpracování, tyto informace lze využít pro okamžitou komunikaci klientovi v procesu zpracování objednávky. Stavy zakázky jsou přístupné přes API rozhraní. ERP systém (a potažmo klient) tak může kdykoliv zjistit, kdy přibližně dojde k expedici, že expedice byla započata a úspěšně dokončena nebo zdržena, tracking ID zakázky pro okamžitou konfirmaci přepravní společnosti, to vše za účelem zvýšení transparentnosti a rychlosti dodávky.",
			],
			[Locale.en]: [
				"Software for automated logistics with the latest artificial intelligence technology and compatible with most storage systems",
				"VertiNode® has many uses, but excels especially in the e-commerce environment. The comprehensive solution allows the integration of AI outputs directly into the company's processes. The ERP system can learn from VertiNode® immediately after the order is handed over for picking to the target picking position and the expected processing time, this information can be used for instant communication with the client in the order processing process. Order statuses are accessible via the API interface. The ERP system (and consequently the client) can find out at any time when the shipment will be dispatched, that the shipment has started and successfully completed or delayed, the tracking ID of the order for immediate confirmation by the carrier, all to increase transparency and delivery speed.",
			],
			[Locale.pl]: [
				"VertiNode® to zaawansowane oprogramowanie logistyczne, które integruje najnowsze technologie sztucznej inteligencji z procesami magazynowymi. System jest kompatybilny z większością systemów magazynowych, zapewniając kompleksową automatyzację i optymalizację operacji intralogistycznych.",
				"VertiNode® znajduje zastosowanie w różnych branżach, jednak szczególnie wyróżnia się w środowisku e-commerce. Pozwala na bezpośrednią integrację wyników sztucznej inteligencji z procesami biznesowymi. Dzięki temu system ERP może natychmiast otrzymać informacje o pozycjach kompletacji i oczekiwanym czasie przetwarzania zamówienia, co zwiększa przejrzystość i szybkość obsługi klienta.",
				"Kluczowe funkcje VertiNode obejmują zarządzanie materiałami, lokalizacjami, ruchem magazynowym i raportowaniem, kompatybilność z różnymi platformami (PC, Mac, aplikacje mobilne), zdalne monitorowanie oraz integrację z systemami ERP i WMS poprzez otwarte API. Dzięki graficznej wizualizacji wydajności oraz nowoczesnym technologiom, VertiNode wspiera kompleksowe zarządzanie magazynem i optymalizację operacji.",
			],
			[Locale.sk]: [
				"Softvér pre automatizovanú logistiku s najnovšou technológiou umelej inteligencie a kompatibilný s väčšinou skladových systémov",
				"VertiNode® má mnoho možností využitia, ale vyniká najmä v prostredí elektronického obchodu. Toto komplexné riešenie umožňuje integrovať výstupy umelej inteligencie priamo do procesov vašej spoločnosti. Systém ERP sa môže od systému VertiNode dozvedieť cieľovú pozíciu vychystávania a očakávaný čas spracovania ihneď po odoslaní objednávky na vychystanie, pričom tieto informácie možno okamžite využiť na komunikáciu s klientom v procese spracovania objednávky. Stavy objednávok sú prístupné prostredníctvom rozhrania API. Systém ERP (a následne klient) tak môže kedykoľvek zistiť, kedy približne dôjde k expedícii, že expedícia bola začatá a úspešne dokončená alebo oneskorená, sledovať ID objednávky na okamžité potvrdenie prepravnou spoločnosťou, a to všetko na zvýšenie transparentnosti a rýchlosti dodania.",
			],
		},
		cards: [
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Úspora nákladů",
					[Locale.en]: "Cost savings",
					[Locale.pl]: "Oszczędność kosztów",
					[Locale.sk]: "Úspora nákladov",
				},
				points: [
					{
						[Locale.cs]: "Nižší provozní náklady",
						[Locale.en]: "Lower operating costs",
						[Locale.pl]: "Niższe koszty operacyjne",
						[Locale.sk]: "Nižšie prevádzkové náklady",
					},
					{
						[Locale.cs]: "Rychlá návratnost investice",
						[Locale.en]: "Quick return on investment",
						[Locale.pl]: "Krótki czas zwrotu inwestycj",
						[Locale.sk]: "Rýchla návratnosť investície",
					},
					{
						[Locale.cs]: "Větší skladová plocha",
						[Locale.en]: "Larger storage area",
						[Locale.pl]: "Wzrost wydajności",
						[Locale.sk]: "Väčšia skladová plocha",
					},
					{
						[Locale.cs]: "Rychlá montáž a uvedení do provozu",
						[Locale.en]: "Quick assembly and commissioning",
						[Locale.pl]: "Szybka instalacja i uruchomienie",
						[Locale.sk]: "Rýchla montáž a uvedenie do prevádzky",
					},
					{
						[Locale.cs]: "Lepší kontrola uloženého zboží",
						[Locale.en]: "Better control of stored goods",
						[Locale.pl]: "Lepsza kontrola przechowywanych artykułów",
						[Locale.sk]: "Lepšia kontrola uloženého tovaru",
					},
					{
						[Locale.cs]: "Snížení reklamačních nákladů",
						[Locale.en]: "Reduction of complaint costs",
						[Locale.pl]: "Niższe koszty reklamacji",
						[Locale.sk]: "Zníženie reklamačných nákladov",
					},
					{
						[Locale.cs]: "Snížení pracovních nákladů",
						[Locale.en]: "Reduction of labor costs",
						[Locale.pl]: "Wydajność zależna od sezonu",
						[Locale.sk]: "Zníženie pracovných nákladov",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Zrychlení procesů",
					[Locale.en]: "Process acceleration",
					[Locale.pl]: "Przyspieszenie procesów",
					[Locale.sk]: "Zrýchlenie procesov",
				},
				points: [
					{
						[Locale.cs]: "Kratší časy pro vychystání",
						[Locale.en]: "Shorter picking times",
						[Locale.pl]: "Krótsze czasy kompletacji",
						[Locale.sk]: "Kratšie časy pre vychystávanie",
					},
					{
						[Locale.cs]: "Doprava materiálu v prostojích",
						[Locale.en]: "Material transport in standstills",
						[Locale.pl]: "Transport materiałów w czasie przestojów",
						[Locale.sk]: "Doprava materiálu v prestojoch",
					},
					{
						[Locale.cs]: "Rychlejší kompletace",
						[Locale.en]: "Faster completion",
						[Locale.pl]: "Szybszy montaż",
						[Locale.sk]: "Rýchlejšia kompletácia",
					},
					{
						[Locale.cs]: "Vyšší produktivita skladu",
						[Locale.en]: "Higher warehouse productivity",
						[Locale.pl]: "Bardziej wydajna obsługa zapasów",
						[Locale.sk]: "Vyššia produktivita skladu",
					},
					{
						[Locale.cs]: "Kratší pochůzky operátorů",
						[Locale.en]: "Shorter operator walks",
						[Locale.pl]: "Krótsze odległości pokonywane przez operatorów",
						[Locale.sk]: "Kratšie pochôdzky operátorov",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Lepší využití zdrojů",
					[Locale.en]: "Better resource utilization",
					[Locale.pl]: "Lepsze wykorzystanie zasobów",
					[Locale.sk]: "Lepšie využitie zdrojov",
				},
				points: [
					{
						[Locale.cs]: "Zlepšení kvality obsluhy",
						[Locale.en]: "Improving service quality",
						[Locale.pl]: "Wyższy poziom obsługi",
						[Locale.sk]: "Zlepšenie kvality obsluhy",
					},
					{
						[Locale.cs]: "Větší přesnost dodávek",
						[Locale.en]: "Greater delivery accuracy",
						[Locale.pl]: "Większa precyzja dostaw",
						[Locale.sk]: "Väčšia presnosť dodávok",
					},
					{
						[Locale.cs]: "Méně chyb při kompletaci",
						[Locale.en]: "Fewer errors in completion",
						[Locale.pl]: "Mniej nieprawidłowych pobrań",
						[Locale.sk]: "Menej chýb pri kompletácii",
					},
					{
						[Locale.cs]: "Kompletace typu k člověku",
						[Locale.en]: "Completion type to human",
						[Locale.pl]: "Pływające lokalizacje magazynowe",
						[Locale.sk]: "Kompletácia typom ku človeku",
					},
					{
						[Locale.cs]: "Větší možnosti sledování zboží",
						[Locale.en]: "Greater tracking options",
						[Locale.pl]: "Zwiększona identyfikowalność",
						[Locale.sk]: "Väčšia možnosť sledovania tovaru",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					[Locale.cs]: "Lepší pracovní prostředí",
					[Locale.en]: "Better working environment",
					[Locale.pl]: "Lepsze środowisko pracy",
					[Locale.sk]: "Lepšie pracovné prostredie",
				},
				points: [
					{
						[Locale.cs]: "Krátký čas na proškolení",
						[Locale.en]: "Short training time",
						[Locale.pl]: "Krótki czas szkolenia",
						[Locale.sk]: "Krátky čas na preškolenie",
					},
					{
						[Locale.cs]: "Lepší ergonomie práce",
						[Locale.en]: "Better ergonomics of work",
						[Locale.pl]: "Lepsza ergonomia",
						[Locale.sk]: "Lepšia ergonómia",
					},
					{
						[Locale.cs]: "Méně absencí z důvodu nemoci",
						[Locale.en]: "Less sick leave",
						[Locale.pl]: "Mniej zwolnień lekarskich",
						[Locale.sk]: "Menšia absencia z dôvodu choroby",
					},
					{
						[Locale.cs]: "Příslušenství pro kompletaci usnadňující vyhledávání zboží",
						[Locale.en]: "Completion accessories facilitating the search for goods",
						[Locale.pl]: "Elastyczny wybór metod kompletacji",
						[Locale.sk]: "Príslušenstvo pre kompletáciu zjednodušujúce vyhľadávanie tovaru",
					},
					{
						[Locale.cs]: "Menší riziko zraňení a úrazu",
						[Locale.en]: "Less risk of injury and accident",
						[Locale.pl]: "Zmniejszone ryzyko obrażeń ciała",
						[Locale.sk]: "Menšie riziko zranenia a úrazu",
					},
				],
			},
		],
	},
};
